import React, {ChangeEvent, useState, useEffect} from 'react';
import wrap from 'wraptile';
import Grid from '@material-ui/core/Grid';
import {TimespanCategory} from '@pluto/rest';
import {ProgressivePlayer} from '@accurate-player/accurate-player-progressive';
import {ApTimelineBasic} from '@accurate-player/accurate-player-components-external';
import Timeline from './Timeline/Timeline';
import QCTabs from './QCTabs/QCTabsConnected';
import PlayerControls from './PlayerControls/PlayerControlsConnected';
import AccuratePlayerConnected from './AccuratePlayer/AccuratePlayerConnected';
import {OnTimelineMarkerClick} from './Timeline/TimelineTypes';
import {AudioTracksClick, TempChannel} from './Timeline/AudioTracks/AudioTracksTypes';
import {OnEditMarkerProps} from './VideoAudioQCContainer';
import {Item} from './QCMedia/Redux/QCMediaActions';
import AdOnPlayingFrame from './AdOnPlayingFrame/AdOnPlayingFrameConnected';
import {PlayerRef} from './AccuratePlayer/AccuratePlayerTypes';
import {
    SpeedChangeProps,
    HandleEffectProps,
    PLAYBACK_SPEED_OPTIONS,
    DEFAULT_PLAYBACK_SPEED,
    PlayerLink,
} from './VideoAudioQCTypes';
import './VideoAudioQC.scss';

export interface VideoAudioQCProps {
    item: Item | null;
    markupIssueTypes: TimespanCategory[];
    onPlayerCreated: (player: ProgressivePlayer) => void;
    onPlayerDestroyed: () => void;
    onStepOneFrameBack: () => void;
    onStepOneFrameForward: () => void;
    onPlay: () => void;
    onPause: () => void;
    onFiveSecondsForward: () => void;
    onFiveSecondsBack: () => void;
    onVolumeChange: () => void;
    onToggleMute: () => void;
    onFullscreen: () => void;
    onSetIn: () => void;
    onSetOut: () => void;
    onClearPoints: () => void;
    onCreateQCNote: (rowId: string) => void;
    onCreateAdBreak: (rowId: string) => void;
    onCreateMarkup: (rowId: string) => void;
    onEditTabMarkerIn: (props: OnEditMarkerProps) => void;
    onEditTabMarkerOut: (props: OnEditMarkerProps) => void;
    onGoToFrame: (frame?: number) => Promise<void>;
    onGoToIn: () => void;
    onGoToOut: () => void;
    onTimelineCreated: (timeline: ApTimelineBasic) => void;
    onTimelineDestroyed: () => void;
    onThumbnailClick: OnTimelineMarkerClick;
    onAudioTrackClick: AudioTracksClick;
    onAudioChannelClick: TempChannel;
    jobDeliveryDate: string;
    playerRef: PlayerRef;
    onGetSnapshot: () => void;
}

export const setDecreaseSpeed = wrap(
    (player: PlayerLink, playbackOptions: number[], prev: number) => {
        const nextIndex = playbackOptions.indexOf(prev) - 1;

        if (player.current?.api && nextIndex >= 0) {
            player.current.api.setPlaybackRate(playbackOptions[nextIndex]);
            return playbackOptions[nextIndex];
        }
        return prev;
    }
);

export const setIncreaseSpeed = wrap(
    (player: PlayerLink, playbackOptions: number[], prev: number) => {
        const nextIndex = playbackOptions.indexOf(prev) + 1;

        if (player.current?.api && nextIndex < playbackOptions.length) {
            player.current.api.setPlaybackRate(playbackOptions[nextIndex]);
            return playbackOptions[nextIndex];
        }
        return prev;
    }
);

export const onSpeedChange = wrap(
    (
        props: SpeedChangeProps,
        setSpeed: (player: PlayerLink, playbackOptions: number[]) => number
    ) => {
        const {player, setPlaybackSpeed, playbackOptions} = props;

        setPlaybackSpeed(setSpeed(player, playbackOptions));
    }
);

export const onSpeedReset = wrap((props: SpeedChangeProps) => {
    const {player, setPlaybackSpeed} = props;

    if (player.current?.api && !player.current.api.getStatus().paused) {
        player.current.api.pause();
        player.current.api.setPlaybackRate(DEFAULT_PLAYBACK_SPEED);
        setPlaybackSpeed(DEFAULT_PLAYBACK_SPEED);
    }
});

export const onPlaybackSpeedChange = wrap(
    (props: SpeedChangeProps, event: ChangeEvent<HTMLSelectElement>) => {
        const {player, setPlaybackSpeed} = props;

        const nextSpeed = Number(event.target.value);

        if (player.current?.api.setPlaybackRate) {
            setPlaybackSpeed(nextSpeed);
            player.current.api.setPlaybackRate(nextSpeed);
        }
    }
);

export const onEffect = wrap((props: HandleEffectProps) => {
    const {playerRef, setPlaybackOptions} = props;
    const player = playerRef.current;

    if (player) {
        setPlaybackOptions(Array.from(player.api.playbackRates));
    } else {
        setPlaybackOptions(PLAYBACK_SPEED_OPTIONS);
    }
});

const VideoAudioQC = (props: VideoAudioQCProps) => {
    const {
        item,
        markupIssueTypes,
        onPlayerCreated,
        onPlayerDestroyed,
        onStepOneFrameForward,
        onStepOneFrameBack,
        onPlay,
        onPause,
        onFiveSecondsForward,
        onFiveSecondsBack,
        onVolumeChange,
        onToggleMute,
        onFullscreen,
        onSetIn,
        onSetOut,
        onClearPoints,
        onCreateQCNote,
        onCreateAdBreak,
        onCreateMarkup,
        onEditTabMarkerIn,
        onEditTabMarkerOut,
        onGoToFrame,
        onGoToIn,
        onGoToOut,
        onTimelineCreated,
        onTimelineDestroyed,
        onThumbnailClick,
        onAudioTrackClick,
        onAudioChannelClick,
        jobDeliveryDate,
        playerRef,
        onGetSnapshot,
    } = props;

    const [playbackSpeed, setPlaybackSpeed] = useState(DEFAULT_PLAYBACK_SPEED);
    const [playbackOptions, setPlaybackOptions] = useState<number[]>([]);

    const handleEffect = onEffect({
        playerRef,
        setPlaybackOptions,
    });

    useEffect(handleEffect, [markupIssueTypes]);

    const speedProps = {
        player: playerRef,
        setPlaybackSpeed,
        playbackOptions,
    } as SpeedChangeProps;

    const handlePlaybackSpeedChange = onPlaybackSpeedChange(speedProps);
    const handleIncreaseSpeed = onSpeedChange(speedProps, setIncreaseSpeed);
    const handleDecreaseSpeed = onSpeedChange(speedProps, setDecreaseSpeed);
    const handleSpeedReset = onSpeedReset(speedProps);

    if (!item || markupIssueTypes.length === 0) return <></>;

    return (
        <div className="qc-root">
            <AdOnPlayingFrame item={item} />

            <Grid container spacing={2} className="video-grid-area">
                <Grid item xs={6} className="video-grid-item">
                    <AccuratePlayerConnected
                        item={item}
                        jobDeliveryDate={jobDeliveryDate}
                        onPlayerCreated={onPlayerCreated}
                        onPlayerDestroyed={onPlayerDestroyed}
                        onSetIn={onSetIn}
                        onSetOut={onSetOut}
                        onGoToFrame={onGoToFrame}
                        onCreateQCNote={onCreateQCNote}
                        onCreateAdBreak={onCreateAdBreak}
                        onCreateMarkup={onCreateMarkup}
                        onIncreaseSpeed={handleIncreaseSpeed}
                        onDecreaseSpeed={handleDecreaseSpeed}
                        onSpeedReset={handleSpeedReset}
                        onGetSnapshot={onGetSnapshot}
                    />
                </Grid>
                <Grid item xs={6} className="tabs-grid-item">
                    <QCTabs
                        item={item}
                        jobDeliveryDate={jobDeliveryDate}
                        onCreateQCNote={onCreateQCNote}
                        onCreateAdBreak={onCreateAdBreak}
                        onCreateMarkup={onCreateMarkup}
                        onEditMarkerIn={onEditTabMarkerIn}
                        onEditMarkerOut={onEditTabMarkerOut}
                        onGoToMarker={onGoToFrame}
                    />
                </Grid>
            </Grid>

            <PlayerControls
                onGetSnapshot={onGetSnapshot}
                jobDeliveryDate={jobDeliveryDate}
                onStepOneFrameBack={onStepOneFrameBack}
                onStepOneFrameForward={onStepOneFrameForward}
                onPlay={onPlay}
                onPause={onPause}
                onFiveSecondsForward={onFiveSecondsForward}
                onFiveSecondsBack={onFiveSecondsBack}
                onVolumeChange={onVolumeChange}
                onToggleMute={onToggleMute}
                onFullscreen={onFullscreen}
                onSetIn={onSetIn}
                onSetOut={onSetOut}
                onClearPoints={onClearPoints}
                onCreateQCNote={onCreateQCNote}
                onCreateAdBreak={onCreateAdBreak}
                onCreateMarkup={onCreateMarkup}
                onGoToIn={onGoToIn}
                onGoToOut={onGoToOut}
                playbackSpeed={playbackSpeed}
                onPlaybackSpeedChange={handlePlaybackSpeedChange}
                playbackOptions={playbackOptions}
            />

            <Timeline
                item={item}
                onTimelineCreated={onTimelineCreated}
                onTimelineDestroyed={onTimelineDestroyed}
                onThumbnailClick={onThumbnailClick}
                onAudioTrackClick={onAudioTrackClick}
                onAudioChannelClick={onAudioChannelClick}
            />
        </div>
    );
};

export default VideoAudioQC;
